import { useEffect, useRef } from 'react';
import { Box, Collapse, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PaymentMethodList from '../paymentMethodList';
import PaymentMethodHeader from '../paymentMethodHeader';
import LocaleKeys from '../../../locales/keys';
import { useTranslation } from 'react-i18next';
import {
  getCreditCardIconByType,
  stripeCardBrandImageUrls,
} from '../../../utils/common';
import ErrorScreen from '../../../common/errorScreen';
import sanitizeHtml from 'sanitize-html';
import { getPaymentMethodDescriptionTextNode } from '../../helpers/subscriptionBasedProductCodeTables';
import PaymentMethodStripe from '../paymentMethodStripe';
import { Elements } from '@stripe/react-stripe-js';
import { paymentMethodSaveButtonId } from '../../../constants/subscription.constant';
import { stripeStyles } from '../../../checkout/containers/stripeStyles';
import { useTheme } from '@material-ui/core/styles';
import {
  getStripePaymentList,
  stripeFonts,
} from '../../../utils/stripeUtility';
import { stripeElementsConfig } from '../../../constants/stripePayment.constant';
import { setElementsOptions } from '../../helpers/paymentMethodStripeHelpers';

const useStyles = makeStyles((theme) => ({
  paymentMethodLabelNode: {
    fontFamily: 'Formula1-Regular',
    WebkitFontSmoothing: 'auto',
    fontSize: '25px',
    textTransform: 'uppercase',
    fontWeight: 500,
    [theme.breakpoints.between('xs', 'md')]: {
      fontSize: '19px',
    },
  },
  paymentMethodContainer: {
    [theme.breakpoints.between('xs', 'md')]: {
      marginBottom: 0,
    },
  },
}));

const PaymentMethod = ({
  classes,
  paymentMethodList,
  handleChange,
  isChangeClicked,
  paymentInstrumentLength,
  handleSelectClick,
  handleEditClick,
  editSelectedId,
  handleButtonCancel,
  paymentMethodFormFault,
  showRemovePaymentInstrumentFault,
  language,
  externalDataState,
  isFreeTrial,
  subscriptionProps,
  isAccountNumber,
  editCardDetailsLabel,
  isShowDefault = false,
  handleChangeWithoutAccountNumber = false,
  isPaymentTemplate = false,
  stripePromise,
  handleSaveStripePayment,
  handleStripeCheckoutSave,
  isStripePaymentInstrumentEnabled,
  stripePaymentList,
  isCheckout,
  updatePaymentInstrumentFault,
  updatePaymentInstrumentUiModel,
  submitOrderUpdateCountryFault,
  isHideSelect,
  completeOrderPaymentMethodEditStripeSubmit,
  subId,
  createdPaymentInstrument,
  setCreatedPaymentInstrument,
  formik,
  isPaymentReady,
  setIsPaymentReady,
  updatePaymentInteractionData,
  submitOrderInteractionData,
  allSubscriptions,
  upgradePlanState,
}) => {
  const { t } = useTranslation();
  const paymentMethodClasses = useStyles();
  const setFocusref = useRef([]);
  const elementOptions = useRef(null);
  const theme = useTheme();
  const paymentMethodFadeIn = (item, editSelectedId) =>
    !editSelectedId || !(item?.PaymentInstrumentId === editSelectedId);

  const isPaymentCardDetailsShown =
    !subscriptionProps.isAffiliateplan && !subscriptionProps.isIap;

  const isPaymentMethodPanelShown =
    subscriptionProps.isAffiliateplan || subscriptionProps.isIap;

  const paymentMethodDescriptionTextNode =
    getPaymentMethodDescriptionTextNode(subscriptionProps);

  const pricingPlanCurrency =
    subscriptionProps?.pricingPlanCurrency?.toLowerCase();
  const paymentMethodConfiguration =
    stripePaymentList?.StripePayment?.ConfigurationId;
  const isCreditCardToStripe = subscriptionProps.isCreditCardToStripe;
  const {
    mode,
    captureMethod,
    paymentMethodCreation,
    defaultStripeCurrency,
    allowedCardBrands,
  } = stripeElementsConfig;

  useEffect(() => {
    if (Object.keys(subscriptionProps).length && !elementOptions.current) {
      elementOptions.current = setElementsOptions(
        subscriptionProps?.pricingPlanChargeAmount,
        mode,
        pricingPlanCurrency,
      );
    }
  }, [subscriptionProps]);

  return (
    <Box>
      {isAccountNumber || isShowDefault || handleChangeWithoutAccountNumber ? (
        <Box className={paymentMethodClasses.paymentMethodContainer}>
          <PaymentMethodHeader
            classes={classes}
            paymentMethodClasses={paymentMethodClasses}
            handleChange={handleChange}
            isChangeClicked={isChangeClicked}
            paymentInstrumentLength={paymentInstrumentLength}
            paymentMethodLabel={
              isPaymentTemplate
                ? t(LocaleKeys.payment.orders.payments)
                : paymentMethodDescriptionTextNode.headerText
            }
            buttonLabel={t(LocaleKeys.subscriptionInfo.subscriptions.change)}
            isFreeTrial={isFreeTrial}
            subscriptionProps={subscriptionProps}
            isPaymentTemplate={isPaymentTemplate}
          />

          {showRemovePaymentInstrumentFault && (
            <ErrorScreen
              error={showRemovePaymentInstrumentFault}
              variant="h2"
              removeStyle={true}
            />
          )}
          {isPaymentCardDetailsShown &&
            paymentMethodList.map((item, index) => (
              <Box
                key={`${item?.PaymentInstrumentId}-${
                  item?.PaymentMethodId
                    ? item?.AccountNumber?.slice(-4)
                    : item?.AccountNumber?.slice(6)
                }-${item?.PaymentMethodId || ''}`}
              >
                <Box>
                  <Collapse
                    in={
                      paymentMethodFadeIn(item, editSelectedId) &&
                      !isShowDefault
                    }
                  >
                    <Box>
                      {
                        <PaymentMethodList
                          classes={classes}
                          cardTypeIcon={
                            item?.PaymentMethodId
                              ? `${stripeCardBrandImageUrls(
                                  getStripePaymentList(
                                    item,
                                    stripePaymentList?.StripePayment,
                                  )?.subType || item.Type,
                                )}`
                              : `${getCreditCardIconByType(item?.Type)}`
                          }
                          isChangeClicked={isChangeClicked}
                          handleSelectClick={handleSelectClick}
                          paymentMethodNode={item}
                          paymentMethodIndex={index}
                          handleEditClick={handleEditClick}
                          defaultPaymentCardLabel={t(
                            LocaleKeys.subscriptionInfo.subscriptions
                              .paymentListDefault,
                          )}
                          editCardDetailsLabel={editCardDetailsLabel}
                          selectButtonLabel={t(
                            LocaleKeys.subscriptionInfo.subscriptions.select,
                          )}
                          isPaymentTemplate={isPaymentTemplate}
                          isStripeAccount={subscriptionProps?.isStripeAccount}
                          stripePaymentMethodType={item?.PaymentMethodType}
                          isHideSelect={isHideSelect}
                          isPaymentReady={isPaymentReady}
                        />
                      }
                    </Box>
                  </Collapse>
                </Box>
                <div>
                  <Box pb={0}>
                    <Collapse
                      in={
                        item?.PaymentInstrumentId === editSelectedId ||
                        isShowDefault
                      }
                      onEntered={() => {
                        setFocusref?.current[index] &&
                          setFocusref?.current[index].focus();
                      }}
                      data-testid="payment-method-collapse"
                    >
                      <Box>
                        <>
                          {elementOptions.current && (
                            <Elements
                              stripe={stripePromise}
                              options={{
                                mode: elementOptions.current?.mode,
                                currency:
                                  pricingPlanCurrency || defaultStripeCurrency,
                                amount: elementOptions.current?.amount,
                                allowedCardBrands,
                                captureMethod,
                                paymentMethodCreation,
                                payment_method_configuration:
                                  paymentMethodConfiguration,
                                appearance: stripeStyles(theme),
                                locale: language,
                                fonts: stripeFonts,
                              }}
                            >
                              <PaymentMethodStripe
                                isCheckout={isCheckout}
                                handleButtonCancel={handleButtonCancel}
                                paymentInstrumentId={item?.PaymentInstrumentId}
                                paymentMethodSaveButtonId={
                                  paymentMethodSaveButtonId
                                }
                                editSelectedId={editSelectedId}
                                handleSaveStripePayment={
                                  handleSaveStripePayment
                                }
                                stripePaymentMethodType={
                                  item?.PaymentMethodType
                                }
                                paymentMethodFormFault={paymentMethodFormFault}
                                handleStripeCheckoutSave={
                                  handleStripeCheckoutSave
                                }
                                isStripePaymentInstrumentEnabled={
                                  isStripePaymentInstrumentEnabled
                                }
                                updatePaymentInstrumentFault={
                                  updatePaymentInstrumentFault
                                }
                                updatePaymentInstrumentUiModel={
                                  updatePaymentInstrumentUiModel
                                }
                                submitOrderUpdateCountryFault={
                                  submitOrderUpdateCountryFault
                                }
                                completeOrderPaymentMethodEditStripeSubmit={
                                  completeOrderPaymentMethodEditStripeSubmit
                                }
                                isCreditCardToStripe={isCreditCardToStripe}
                                subId={subId}
                                paymentMethod={item}
                                createdPaymentInstrument={
                                  createdPaymentInstrument
                                }
                                setCreatedPaymentInstrument={
                                  setCreatedPaymentInstrument
                                }
                                formik={formik}
                                setIsPaymentReady={setIsPaymentReady}
                                language={language}
                                externalDataStates={externalDataState}
                                updatePaymentInteractionData={
                                  updatePaymentInteractionData
                                }
                                submitOrderInteractionData={
                                  submitOrderInteractionData
                                }
                                currency={
                                  pricingPlanCurrency || defaultStripeCurrency
                                }
                                amount={elementOptions.current?.amount}
                                stripePaymentList={stripePaymentList}
                                allSubscriptions={allSubscriptions}
                                upgradePlanState={upgradePlanState}
                              />
                            </Elements>
                          )}
                        </>
                      </Box>
                    </Collapse>
                  </Box>
                </div>
                {isPaymentTemplate && isChangeClicked && (
                  <Divider style={{ margin: '10px 15px' }} />
                )}
              </Box>
            ))}
          {isPaymentMethodPanelShown && (
            <Box mt={5} display="flex">
              <Typography variant="body1">
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      paymentMethodDescriptionTextNode.descriptionText,
                    ),
                  }}
                />
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant="body1">
          {t(LocaleKeys.subscriptionInfo.subscriptions.noPaymentDetails)}
        </Typography>
      )}
    </Box>
  );
};

export default PaymentMethod;
